import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ProjectForm from '../../components/ProjectForm'; // Import the form

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth(); // Get the auth token
  const [error, setError] = useState(null);

  // Add new states for the form modal
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);

  // States for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(res.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setError('Failed to fetch projects.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleOpenForm = (project = null) => {
    setProjectToEdit(project);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setProjectToEdit(null);
    setIsFormOpen(false);
  };

  const handleSave = async (formData, projectId) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      if (projectId) {
        // Update existing project
        await axios.put(`http://localhost:5001/api/projects/${projectId}`, formData, config);
      } else {
        // Create new project
        await axios.post('http://localhost:5001/api/projects', formData, config);
      }

      fetchProjects(); // Refresh list
      handleCloseForm(); // Close modal
    } catch (error) {
      console.error('Failed to save project:', error);
      alert('Failed to save project.');
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteProjectId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteProjectId(null);
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (deleteProjectId) {
      await handleDelete(deleteProjectId);
    }
    handleCloseDeleteDialog();
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`http://localhost:5001/api/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Refresh project list after deleting
      fetchProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Header with Add Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Manage Projects</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenForm()}>
          Add New Project
        </Button>
      </Box>

      {/* Error Message */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Projects Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              projects.map((project) => (
                <TableRow key={project._id}>
                  <TableCell>{project.title}</TableCell>
                  <TableCell>{project.tags.join(', ')}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit Project">
                      <IconButton color="primary" onClick={() => handleOpenForm(project)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Project">
                      <IconButton color="error" onClick={() => handleOpenDeleteDialog(project._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Project Form Modal */}
      <ProjectForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSave={handleSave}
        project={projectToEdit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this project?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectsPage;
